import React, { useEffect, useState, useCallback } from "react";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MessageSquare, Gift, Loader2, Save, UserPlus, UserMinus, Repeat, Ticket, Star } from "lucide-react";
import { couponBadge, emptyCoupon } from "@/lib/coupon";
import CouponConfigForm from "@/components/CouponConfigForm";
import MessagesTabs from "@/components/MessagesTabs";
import { toast } from "sonner";

const RULES = [
  { key: "follow", title: "Quando un cliente ti segue", icon: UserPlus, sub: "Messaggio di benvenuto automatico" },
  { key: "unfollow", title: "Quando smette di seguirti", icon: UserMinus, sub: "Un ultimo saluto per riconquistarlo" },
  { key: "recurring", title: "Messaggio ricorrente", icon: Repeat, sub: "Ricontatta chi ti segue ogni tot giorni" },
];

export default function VendorLoyalty() {
  const [rules, setRules] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [am, tp] = await Promise.all([
      api.get("/vendor/auto-messages", { headers: authHeaders("vendor") }),
      api.get("/vendor/coupon-templates", { headers: authHeaders("vendor") }),
    ]);
    setRules(am.data.auto_messages);
    setTemplates(tp.data.templates || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (key, patch) => setRules((r) => ({ ...r, [key]: { ...r[key], ...patch } }));
  const couponSel = (r) => r.coupon_template_id || "new";
  const setCouponSel = (key, val) => {
    if (val === "new") {
      const r = rules[key];
      upd(key, { coupon_template_id: null, coupon: { ...emptyCoupon(), title: r.coupon_text || "", discount_value: r.coupon_discount || 10, ...(r.coupon || {}) } });
    } else {
      upd(key, { coupon_template_id: val });
    }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      const r = await api.put("/vendor/auto-messages", rules, { headers: authHeaders("vendor") });
      if (r.data.auto_messages) setRules(r.data.auto_messages);
      if (r.data.coupon_templates) setTemplates(r.data.coupon_templates);
      toast.success("Messaggi automatici salvati");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (!rules) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  return (
    <div className="space-y-6 kn-fade-up max-w-3xl" data-testid="vendor-loyalty-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Messaggi Fedeltà</h1>
        <p className="text-slate-500">Coltiva la relazione con chi ti segue, in automatico.</p>
      </div>
      <MessagesTabs />

      <div className="space-y-3">
        <div className="flex items-center gap-2 font-bold text-slate-800 px-1"><MessageSquare className="h-5 w-5 text-[#E63946]" /> Messaggi automatici</div>
        {RULES.map(({ key, title, icon: Icon, sub }) => {
          const r = rules[key];
          return (
            <div key={key} className="kn-card p-4 space-y-3" data-testid={`rule-${key}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-slate-600" /></div>
                <div className="flex-1 min-w-0"><p className="font-bold text-slate-800">{title}</p><p className="text-xs text-slate-400">{sub}</p></div>
                <Switch checked={r.enabled} onCheckedChange={(v) => upd(key, { enabled: v })} data-testid={`rule-${key}-enabled`} />
              </div>
              {r.enabled && (
                <div className="space-y-3 pl-1">
                  <Textarea value={r.message} onChange={(e) => upd(key, { message: e.target.value })} rows={2} placeholder="Scrivi il messaggio..." className="rounded-xl" data-testid={`rule-${key}-message`} />
                  {key === "recurring" && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ogni (giorni)</Label>
                      <Input type="number" min="1" value={r.interval_days || 30} onChange={(e) => upd(key, { interval_days: Number(e.target.value) })} className="h-10 rounded-xl w-32" />
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-[#FFF1F2]">
                    <div className="flex items-center gap-2"><Gift className="h-4 w-4 text-[#E63946]" /><span className="font-semibold text-slate-700 text-sm">Includi coupon fedeltà</span></div>
                    <Switch checked={r.coupon_enabled} onCheckedChange={(v) => upd(key, { coupon_enabled: v })} data-testid={`rule-${key}-coupon`} />
                  </div>
                  {r.coupon_enabled && (
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5" /> Scegli un coupon</Label>
                        <Select value={couponSel(r)} onValueChange={(v) => setCouponSel(key, v)}>
                          <SelectTrigger className="h-11 rounded-xl" data-testid={`rule-${key}-coupon-select`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">➕ Crea un nuovo coupon</SelectItem>
                            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title || "Coupon"} · {couponBadge(t)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {r.coupon_template_id ? (
                        (() => {
                          const t = templates.find((x) => x.id === r.coupon_template_id);
                          if (!t) return <p className="text-xs text-amber-600">Coupon predefinito non trovato. Scegline un altro o creane uno nuovo.</p>;
                          return (
                            <div className="rounded-xl border border-slate-100 p-3 flex items-center gap-3" data-testid={`rule-${key}-coupon-preview`}>
                              <div className="h-11 w-11 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                                {(t.images || [])[0] ? <img src={t.images[0]} alt="" className="h-full w-full object-cover" /> : <Ticket className="h-5 w-5 text-slate-400" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-slate-800 truncate">{t.title || "Coupon"}</p>
                                <p className="text-xs text-slate-400 truncate">{t.description || "Coupon predefinito"}</p>
                              </div>
                              <span className="px-2.5 h-7 rounded-full bg-[#FFE4E6] text-[#BE123C] font-extrabold text-xs flex items-center shrink-0">{couponBadge(t)}</span>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="rounded-xl border border-slate-100 p-3 space-y-3" data-testid={`rule-${key}-coupon-form`}>
                          <CouponConfigForm value={r.coupon || emptyCoupon()} onChange={(patch) => upd(key, { coupon: { ...(r.coupon || emptyCoupon()), ...patch } })} idPrefix={`rule-${key}`} />
                          <label className="flex items-center justify-between gap-2 rounded-xl bg-[#FFF7F8] p-3">
                            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Star className="h-4 w-4 text-[#E63946]" /> Aggiungi ai preferiti</span>
                            <Switch checked={!!r.save_to_favorites} onCheckedChange={(v) => upd(key, { save_to_favorites: v })} data-testid={`rule-${key}-save-fav`} />
                          </label>
                          <p className="text-[11px] text-slate-400 -mt-1">Salvandolo tra i preferiti lo ritrovi nei "Coupon predefiniti" e potrai riusarlo.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <Button onClick={saveRules} disabled={saving} data-testid="save-rules" className="w-full h-12 rounded-xl font-bold gap-2">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Salva messaggi automatici
        </Button>
      </div>
    </div>
  );
}
